import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge class names with Tailwind CSS classes
 * This function combines clsx and tailwind-merge to handle class conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a numeric amount as currency for a given locale and currency code.
 * Amount is always in major units (e.g. 10000.59 is $10,000.59), not cents.
 * @param locale - BCP 47 locale (e.g. "en-AU", "ja-JP", "zh-TW")
 * @param currency - ISO 4217 currency code (e.g. "AUD", "USD", "aud")
 * @param amount - Amount in major units (e.g. 10000.59 for $10,000.59)
 */
export function formatCurrency(locale: string, currency: string, amount: number): string {
  try {
    const code = (currency || 'USD').toUpperCase();
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${(currency || 'USD').toUpperCase()}`;
  }
}

/**
 * Returns true for date-only strings like "2026-02-24" (no time component).
 * Date-only values (event start/end dates) are safe to format in UTC.
 * Datetime values (schedule times from Supabase as UTC) need browser-local
 * timezone conversion so users see the correct local time.
 */
function isDateOnly(iso: string): boolean {
  return !iso.includes('T');
}

/**
 * Format date in a basic format (e.g., "Jan 15, 2024")
 */
export function formatDate(iso: string, locale: string, timeZone?: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      // Date-only strings always render in UTC (the day must never shift).
      // Datetimes use the caller's timeZone: pass 'UTC' for a hydration-safe
      // SSR/first render, undefined (default) for the viewer's local time.
      timeZone: isDateOnly(iso) ? 'UTC' : timeZone,
    });
  } catch {
    return iso;
  }
}

/**
 * Determine the display date for a live schedule based on event dates
 * If today is before event start, returns start date
 * If today is after event end, returns end date
 * Otherwise returns today
 */
export function getDisplayDateForSchedule(
  eventStartDate: string,
  eventEndDate: string,
  today?: Date,
): Date {
  const currentDate = today || new Date();
  const eventStart = new Date(eventStartDate);
  const eventEnd = new Date(eventEndDate);

  // If today is before event start, show start date
  if (currentDate < eventStart) {
    return eventStart;
  }
  // If today is after event end, show end date
  else if (currentDate > eventEnd) {
    return eventEnd;
  }
  // Otherwise show today
  else {
    return currentDate;
  }
}

/**
 * Format date in a long format with weekday (e.g., "Monday, January 15, 2024")
 */
export function formatDateLong(iso: string, locale: string, timeZone?: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: isDateOnly(iso) ? 'UTC' : timeZone,
    });
  } catch {
    return iso;
  }
}

/**
 * Format date and time (e.g., "Monday, January 15, 2024 at 2:30 PM")
 */
export function formatDateTime(iso: string, locale: string, timeZone?: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;

    return d.toLocaleString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: isDateOnly(iso) ? 'UTC' : timeZone,
    });
  } catch {
    return iso;
  }
}

/**
 * Format duration in seconds to MM:SS format (e.g., "2:30" for 150 seconds)
 */
export function formatDuration(seconds: number): string {
  // Round once, then split, so 59.6s carries into the minute instead of reading ":60".
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}

/**
 * Format time from 24-hour format to 12-hour AM/PM format (e.g., "14:30" to "2:30 PM")
 */
export function formatTime12Hour(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour24 = parseInt(hours);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minutes} ${ampm}`;
}

/**
 * Format time only from ISO string (e.g., "2:30 PM")
 */
export function formatTime(iso: string, locale: string, timeZone?: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    });
  } catch {
    return iso;
  }
}

export function formatRating(r: number | null): string {
  if (r === null || r === undefined) return '—';
  return Math.round(r).toString();
}

export function formatScoreChange(r: number | null): string {
  if (r === null || r === undefined) return '—';

  // For positive values, round up (Math.ceil)
  // For negative values, round down (Math.floor)
  if (r >= 0) {
    return Math.ceil(r).toString();
  } else {
    return Math.floor(r).toString();
  }
}

/**
 * Format a value, returning translated 'N/A' if the value is empty or null
 */
export function formatValue(input?: string | null, t?: (key: string) => string): string {
  const v = (input || '').toString().trim();
  return v.length ? v : t ? t('notAvailable') : 'N/A';
}

/**
 * Format birthday with age calculation
 */
export function formatBirthday(iso?: string | null, t?: (key: string) => string): string {
  if (!iso) return t ? t('notAvailable') : 'N/A';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return t ? t('notAvailable') : 'N/A';

  const age = Math.max(
    0,
    new Date().getFullYear() - date.getFullYear() - (hasNotHadBirthdayThisYear(date) ? 1 : 0),
  );

  const formatted = formatDate(iso, 'en'); // Default to 'en' for consistency
  const ageLabel = t ? t('age') : 'Age';
  return `${formatted} (${ageLabel}: ${age})`;
}

function hasNotHadBirthdayThisYear(birth: Date): boolean {
  const now = new Date();
  if (now.getMonth() < birth.getMonth()) return true;
  if (now.getMonth() > birth.getMonth()) return false;
  return now.getDate() < birth.getDate();
}

/**
 * Format hand preference
 */
export function formatHand(pref?: string | null, t?: (key: string) => string): string {
  if (!pref) return t ? t('notAvailable') : 'N/A';

  if (t) {
    const map: Record<string, string> = {
      left: t('handPreferences.left'),
      right: t('handPreferences.right'),
      ambidextrous: t('handPreferences.ambidextrous'),
    };
    return map[pref] || pref;
  }

  // Fallback for when translation function is not provided
  const map: Record<string, string> = {
    left: 'Left',
    right: 'Right',
    ambidextrous: 'Ambidextrous',
  };
  return map[pref] || pref;
}

/**
 * Convert ISO timestamp string to datetime-local input format
 * datetime-local expects format: YYYY-MM-DDTHH:MM
 */
export function isoToDatetimeLocal(isoString: string | null | undefined): string {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';

    // Format as YYYY-MM-DDTHH:MM
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch {
    return '';
  }
}

/**
 * Convert datetime-local input value to ISO string
 * datetime-local provides format: YYYY-MM-DDTHH:MM
 */
export function datetimeLocalToIso(datetimeLocal: string): string {
  if (!datetimeLocal) return '';
  try {
    const date = new Date(datetimeLocal);
    if (isNaN(date.getTime())) return '';
    return date.toISOString();
  } catch {
    return '';
  }
}

/**
 * Create a URL-friendly slug from a string
 * Handles Chinese characters by preserving them instead of removing them
 * For non-Chinese text, converts to lowercase and replaces spaces with hyphens
 */
export function slugify(input: string): string {
  if (!input) return '';

  // Trim whitespace
  const trimmed = input.trim();

  // Check if the string contains Chinese characters
  const hasChinese = /[\u4e00-\u9fff]/.test(trimmed);

  if (hasChinese) {
    // For Chinese text, just remove extra spaces and replace with hyphens
    // but preserve the Chinese characters
    return trimmed
      .replace(/\s+/g, '-') // Replace multiple spaces with single hyphen
      .replace(/[^\u4e00-\u9fff\w\-]/g, '') // Remove everything except Chinese chars, word chars, and hyphens
      .replace(/(^-+|-+$)/g, ''); // Remove leading/trailing hyphens
  } else {
    // For non-Chinese text, use traditional slugify approach
    return trimmed
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }
}

/**
 * Strip HTML tags from a string and return plain text
 * Handles common HTML entities and preserves line breaks as spaces
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';

  return html
    .replace(/<br\s*\/?>/gi, ' ') // Replace <br> tags with spaces
    .replace(/<\/p>/gi, ' ') // Replace </p> tags with spaces
    .replace(/<\/div>/gi, ' ') // Replace </div> tags with spaces
    .replace(/<\/li>/gi, ' ') // Replace </li> tags with spaces
    .replace(/<\/h[1-6]>/gi, ' ') // Replace closing heading tags with spaces
    .replace(/<[^>]*>/g, '') // Remove all remaining HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with regular spaces
    .replace(/&amp;/g, '&') // Replace &amp; with &
    .replace(/&lt;/g, '<') // Replace &lt; with <
    .replace(/&gt;/g, '>') // Replace &gt; with >
    .replace(/&quot;/g, '"') // Replace &quot; with "
    .replace(/&#39;/g, "'") // Replace &#39; with '
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing whitespace
}

/**
 * Calculate the duration in days between two dates
 * Returns the inclusive count of days (start and end dates are both counted)
 * Handles timezone conversion to local time for accurate day calculation
 * @param startDate - Start date string (ISO format)
 * @param endDate - End date string (ISO format)
 * @returns Number of days or null if dates are invalid
 */
export function calculateDurationDays(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): number | null {
  if (!startDate || !endDate) return null;

  try {
    // Parse dates and convert to local timezone
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    // Convert to local date strings to avoid timezone issues
    // This gives us the date in the user's local timezone
    const startLocal = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    const diffTime = endLocal.getTime() - startLocal.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates

    return diffDays;
  } catch {
    return null;
  }
}

/**
 * Generate array of date strings between start and end dates (inclusive)
 * Handles timezone issues by converting to local time first
 * @param startDate - Start date string (ISO format or YYYY-MM-DD)
 * @param endDate - End date string (ISO format or YYYY-MM-DD)
 * @returns Array of date strings in YYYY-MM-DD format
 */
export function generateDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];

  try {
    // Convert to local dates to handle timezone properly
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    // Check if start date is valid
    if (isNaN(startDateObj.getTime())) {
      console.error('Invalid start date provided:', { startDate, endDate });
      return dates;
    }

    // If end date is invalid, use start date as end date
    const effectiveEndDateObj = isNaN(endDateObj.getTime()) ? startDateObj : endDateObj;

    // Get local date components
    const startYear = startDateObj.getFullYear();
    const startMonth = startDateObj.getMonth() + 1; // getMonth() returns 0-11
    const startDay = startDateObj.getDate();

    const endYear = effectiveEndDateObj.getFullYear();
    const endMonth = effectiveEndDateObj.getMonth() + 1;
    const endDay = effectiveEndDateObj.getDate();

    // Generate date range using local date arithmetic
    let currentYear = startYear;
    let currentMonth = startMonth;
    let currentDay = startDay;

    while (true) {
      // Format current date as YYYY-MM-DD
      const yearStr = currentYear.toString();
      const monthStr = currentMonth.toString().padStart(2, '0');
      const dayStr = currentDay.toString().padStart(2, '0');
      dates.push(`${yearStr}-${monthStr}-${dayStr}`);

      // Check if we've reached the end date
      if (currentYear === endYear && currentMonth === endMonth && currentDay === endDay) {
        break;
      }

      // Move to next day
      currentDay++;

      // Handle month boundaries
      const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
      if (currentDay > daysInMonth) {
        currentDay = 1;
        currentMonth++;

        // Handle year boundaries
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
      }
    }
  } catch (error) {
    console.error('Error generating date range:', error);
  }

  return dates;
}

/**
 * Convert various date formats to ISO format (YYYY-MM-DD)
 * @param dateStr - Date string in various formats
 * @returns Date string in YYYY-MM-DD format
 */
export function convertDateToISO(dateStr: string): string {
  // If it's already in ISO format (YYYY-MM-DD), return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  // Try to parse old format like "Wed, Sep 24" and convert to ISO
  const currentYear = new Date().getFullYear();
  const date = new Date(`${dateStr}, ${currentYear}`);

  // Check if the date is valid AND if the parsed date actually contains the original string
  // This prevents false positives from JavaScript's permissive date parsing
  if (!isNaN(date.getTime()) && dateStr.includes(date.toDateString().split(' ')[1])) {
    return date.toISOString().split('T')[0];
  }

  // Fallback: return the original string
  return dateStr;
}
